import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNotificationsContext } from "../../../contexts/notificationsContext";
import Modal from "../Modal";
import { useNavigate } from "react-router-dom";
import { useMyProjects } from "../../../hooks/useMyProjects";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { notifications, markAllAsRead, markAsRead, fetchNotifications } =
    useNotificationsContext();

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { fetchProjects } = useMyProjects();

  useEffect(() => {
    if (!isOpen) return;

    const fetch = async () => {
      setLoading(true);
      try {
        await fetchNotifications();
      } catch  {
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [isOpen, fetchNotifications]);

  const handleNotificationClick = (notif: any) => {
    if (
      notif.type === "SUPERVISION_INVITE" &&
      notif.supervisionInvite?.status === "PENDING"
    ) {
      return;
    }

    markAsRead(notif.id);
  };

  const handleSupervisorInviteResponse = async (
    inviteId: number,
    projectId: number,
    status: "ACCEPTED" | "DECLINED",
    notifId: number
  ) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_URL}/supervisors/invites/${inviteId}`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "X-Client-Type": "web",
          },
        }
      );

      await markAsRead(notifId);

      await fetchNotifications();

      await fetchProjects();

      if (status === "ACCEPTED") {
        toast.success("Invite accepted");
        navigate(`/projects/${projectId}/edit`);
        onClose();
      } else {
        toast.error("Invite declined");
      }
    } catch (err) {
      toast.error("Failed to update invite status.");
    }
  };

  return (
    <Modal isOpen={isOpen} maxWidth="30rem">
      <div className="flex flex-col max-h-[80vh] w-[30rem]">
        <div className="px-6 pt-6 pb-2 flex items-center justify-between">
          <h2 className="text-lg font-bold">Notifications</h2>
          <button
            onClick={markAllAsRead}
            className="text-sm text-blue-600 hover:underline"
          >
            Mark all as read
          </button>
        </div>

        <div className="p-6 pt-0 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : notifications.length === 0 ? (
            <p className="text-gray-500">No notifications found.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {notifications.map((notif) => (
                <li
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`border rounded-lg p-3 shadow-sm cursor-pointer ${
                    notif.status === "RECEIVED"
                      ? "bg-gray-100"
                      : "bg-white hover:bg-gray-50"
                  }`}
                >
                  <h3 className="font-medium">{notif.title}</h3>
                  <p className="text-sm text-gray-600">{notif.body}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Type: {notif.type} | Status: {notif.status}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(notif.createdAt).toLocaleString()}
                  </p>

                  {notif.type === "SUPERVISION_INVITE" &&
                    notif.supervisionInvite?.id &&
                    notif.supervisionInvite?.projectId &&
                    notif.supervisionInvite?.status === "PENDING" && (
                      <div className="mt-3 flex flex-row gap-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSupervisorInviteResponse(
                              notif.supervisionInvite!.id,
                              notif.supervisionInvite!.projectId,
                              "ACCEPTED",
                              notif.id
                            );
                          }}
                          className="w-full px-3 py-1 bg-buttonBlue text-white text-sm rounded "
                        >
                          Accept
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSupervisorInviteResponse(
                              notif.supervisionInvite!.id,
                              notif.supervisionInvite!.projectId,
                              "DECLINED",
                              notif.id
                            );
                          }}
                          className="w-full px-3 py-1 bg-red-600 text-white text-sm rounded"
                        >
                          Decline
                        </button>
                      </div>
                    )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end bg-white">
          <button
            onClick={onClose}
            className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NotificationsModal;
